import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";

const LessonContainer = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  padding-top: 80px;
`;

const LessonHeader = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  padding: 40px 0;
  border-bottom: 1px solid rgba(255, 215, 0, 0.2);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const LessonTitle = styled.h1`
  font-family: "Playfair Display", serif;
  font-size: 2.5rem;
  color: #ffffff;

  span {
    color: #ffd700;
  }
`;

const LessonProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const ProgressCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: conic-gradient(
    #ffd700 ${(props) => props.progress * 3.6}deg,
    rgba(255, 215, 0, 0.1) 0deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    width: 60px;
    height: 60px;
    background: #1a1a2e;
    border-radius: 50%;
  }
`;

const ProgressText = styled.span`
  position: relative;
  z-index: 1;
  color: #ffd700;
  font-weight: 700;
  font-size: 1.2rem;
`;

const LessonContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 40px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const VideoSection = styled(motion.div)`
  background: #1a1a2e;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 40px;
`;

const VideoPlaceholder = styled.div`
  width: 100%;
  height: 450px;
  background: linear-gradient(
    135deg,
    rgba(255, 215, 0, 0.1) 0%,
    rgba(255, 165, 0, 0.1) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
`;

const VideoControls = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(255, 215, 0, 0.1);
`;

const PlayButton = styled(motion.button)`
  padding: 0.8rem 2rem;
  background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
  color: #0a0a0a;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LessonTabs = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  background: none;
  border: none;
  color: ${(props) => (props.active ? "#FFD700" : "#b0b0b0")};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  transition: color 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 3px;
    background: #ffd700;
    transform: scaleX(${(props) => (props.active ? 1 : 0)});
    transition: transform 0.3s ease;
  }

  &:hover {
    color: #ffd700;
  }
`;

const TabContent = styled(motion.div)`
  background: #1a1a2e;
  padding: 30px;
  border-radius: 20px;
`;

const ExerciseCard = styled(motion.div)`
  background: rgba(255, 215, 0, 0.05);
  padding: 25px;
  border-radius: 15px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 215, 0, 0.1);
`;

const ExerciseTitle = styled.h3`
  color: #ffd700;
  font-size: 1.3rem;
  margin-bottom: 1rem;
`;

const ExerciseDescription = styled.p`
  color: #b0b0b0;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const RecordButton = styled(motion.button)`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${(props) => (props.recording ? "#e74c3c" : "#FFD700")};
  color: #0a0a0a;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 2rem auto;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);

  &:hover {
    transform: scale(1.1);
  }
`;

const Sidebar = styled.div``;

const LessonOutline = styled(motion.div)`
  background: #1a1a2e;
  padding: 30px;
  border-radius: 20px;
  position: sticky;
  top: 100px;
`;

const OutlineTitle = styled.h3`
  color: #ffd700;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
`;

const OutlineItem = styled.div`
  padding: 15px;
  margin-bottom: 10px;
  background: ${(props) =>
    props.active ? "rgba(255, 215, 0, 0.1)" : "transparent"};
  border-radius: 10px;
  border: 1px solid
    ${(props) => (props.active ? "rgba(255, 215, 0, 0.3)" : "transparent")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 215, 0, 0.05);
    border-color: rgba(255, 215, 0, 0.2);
  }
`;

const ItemTitle = styled.div`
  color: #ffffff;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const ItemDuration = styled.div`
  color: #b0b0b0;
  font-size: 0.9rem;
`;

const LessonPage = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [isRecording, setIsRecording] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  const lessonSections = [
    { id: 1, title: "Introduction", duration: "5 min", completed: true },
    {
      id: 2,
      title: "Vocabulary Practice",
      duration: "15 min",
      completed: true,
    },
    { id: 3, title: "Speaking Exercise", duration: "20 min", completed: false },
    {
      id: 4,
      title: "Pronunciation Drill",
      duration: "10 min",
      completed: false,
    },
    { id: 5, title: "Review & Quiz", duration: "10 min", completed: false },
  ];

  const exercises = [
    {
      id: 1,
      title: "Introduce Yourself",
      description:
        "Record a 1-minute introduction about yourself, your background, and your language learning goals.",
      type: "speaking",
    },
    {
      id: 2,
      title: "Describe Your Day",
      description:
        "Tell us about your typical day using present tense verbs and time expressions.",
      type: "speaking",
    },
    {
      id: 3,
      title: "Role Play: Restaurant",
      description:
        "Practice ordering food in a restaurant scenario. Use polite expressions and food vocabulary.",
      type: "roleplay",
    },
  ];

  const handleRecord = () => {
    setIsRecording(!isRecording);
    // Voice recording logic would go here
  };

  return (
    <LessonContainer>
      <LessonHeader>
        <HeaderContent>
          <LessonTitle>
            Lesson {id}: <span>Business Communication</span>
          </LessonTitle>
          <LessonProgress>
            <ProgressCircle progress={40}>
              <ProgressText>40%</ProgressText>
            </ProgressCircle>
          </LessonProgress>
        </HeaderContent>
      </LessonHeader>

      <LessonContent>
        <MainContent>
          <VideoSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <VideoPlaceholder>▶️</VideoPlaceholder>
            <VideoControls>
              <PlayButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>▶️</span> Play Lesson
              </PlayButton>
              <span style={{ color: "#b0b0b0" }}>Duration: 45 minutes</span>
            </VideoControls>
          </VideoSection>

          <LessonTabs>
            <Tab
              active={activeTab === "overview"}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </Tab>
            <Tab
              active={activeTab === "exercises"}
              onClick={() => setActiveTab("exercises")}
            >
              Exercises
            </Tab>
            <Tab
              active={activeTab === "resources"}
              onClick={() => setActiveTab("resources")}
            >
              Resources
            </Tab>
          </LessonTabs>

          <TabContent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === "overview" && (
              <div>
                <h2 style={{ color: "#FFD700", marginBottom: "1.5rem" }}>
                  Lesson Overview
                </h2>
                <p
                  style={{
                    color: "#b0b0b0",
                    lineHeight: 1.8,
                    marginBottom: "2rem",
                  }}
                >
                  In this lesson, you'll learn essential business communication
                  skills including formal greetings, email etiquette, and
                  professional phone conversations. Master the art of confident
                  business English.
                </p>
                <h3 style={{ color: "#FFD700", marginBottom: "1rem" }}>
                  Learning Objectives:
                </h3>
                <ul
                  style={{
                    color: "#b0b0b0",
                    lineHeight: 2,
                    paddingLeft: "20px",
                  }}
                >
                  <li>Use formal business vocabulary correctly</li>
                  <li>Structure professional emails</li>
                  <li>Conduct phone calls with confidence</li>
                  <li>Master business presentation phrases</li>
                </ul>
              </div>
            )}

            {activeTab === "exercises" && (
              <div>
                {exercises.map((exercise, index) => (
                  <ExerciseCard
                    key={exercise.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <ExerciseTitle>{exercise.title}</ExerciseTitle>
                    <ExerciseDescription>
                      {exercise.description}
                    </ExerciseDescription>
                    <RecordButton
                      recording={isRecording}
                      onClick={handleRecord}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {isRecording ? "⏹" : "🎤"}
                    </RecordButton>
                  </ExerciseCard>
                ))}
              </div>
            )}

            {activeTab === "resources" && (
              <div>
                <h2 style={{ color: "#FFD700", marginBottom: "1.5rem" }}>
                  Downloadable Resources
                </h2>
                <div style={{ display: "grid", gap: "1rem" }}>
                  <ResourceItem>📄 Business Vocabulary PDF</ResourceItem>
                  <ResourceItem>🎧 Audio Pronunciation Guide</ResourceItem>
                  <ResourceItem>📝 Email Templates</ResourceItem>
                  <ResourceItem>📊 Practice Worksheets</ResourceItem>
                </div>
              </div>
            )}
          </TabContent>
        </MainContent>

        <Sidebar>
          <LessonOutline
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <OutlineTitle>Lesson Sections</OutlineTitle>
            {lessonSections.map((section, index) => (
              <OutlineItem
                key={section.id}
                active={currentSection === index}
                onClick={() => setCurrentSection(index)}
              >
                <ItemTitle>
                  {section.completed && "✅ "}
                  {section.title}
                </ItemTitle>
                <ItemDuration>{section.duration}</ItemDuration>
              </OutlineItem>
            ))}
          </LessonOutline>
        </Sidebar>
      </LessonContent>
    </LessonContainer>
  );
};

const ResourceItem = styled.div`
  background: rgba(255, 215, 0, 0.05);
  padding: 1.5rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 215, 0, 0.1);
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 215, 0, 0.1);
    border-color: rgba(255, 215, 0, 0.3);
    transform: translateX(10px);
  }
`;

export default LessonPage;
